import images from '../assets/images';

/**
 * Per-product branding for the auth screens.
 *
 * Someone arriving with `?application=linentech` did not set out to "sign in to
 * Nextek" — they clicked Sign In inside LinenTech and expect to keep seeing
 * LinenTech. So the auth card wears the incoming product's lockup and accent
 * colour, and names Nextek Systems underneath as the parent rather than as the
 * thing being signed into.
 *
 * Nothing changes when no product is named: `getBrand(null)` returns
 * `NEXTEK_BRAND`, and `brandCssVars(NEXTEK_BRAND)` is empty — so every card keeps
 * the exact palette it already shipped with, including the deliberate per-step
 * colours of the reset flow (sky 2FA, orange OTP, green new-password).
 *
 * Branding is driven by `?application=` in the URL alone — see hooks/useBrand for
 * why it must not fall back to the remembered copy the way the handoff target
 * does.
 */

export interface Brand {
  /** Lowercased `?application=` value this brand answers to; '' for Nextek. */
  key: string;
  /** Display name in the product's own spelling — matches ProductsTagline. */
  name: string;
  /** Wide lockup for the auth-card header. */
  logo: string;
  /**
   * Rendered lockup height in px. Per-brand because the six lockups have wildly
   * different aspect ratios: the LinenConnect/Desk/Crew wordmarks are ~4:1 and
   * read fine at 40px, while the EquipMate/LinenIQ lockups are ~2.4:1 with a
   * dominant mark, so their names are unreadable below ~58px.
   */
  logoHeight: number;
  /** Drives buttons, links, input focus, title gradient, and backdrop tint. */
  accent: string;
  /** Second gradient stop. */
  accentSecondary: string;
}

/**
 * Accents are each product's own logo colour, darkened where the raw hue could
 * not carry both roles the accent has to play — white text on a filled button,
 * and small link text on white. LinenConnect's `#21C45D` (2.3:1 on white) and
 * LinenIQ's `#17B091` (2.8:1) both failed as link text, so they sit one step
 * darker with the true brand hue kept as the second gradient stop.
 */
const PRODUCT_BRANDS: readonly Brand[] = [
  {
    key: 'linentech',
    name: 'LinenTech',
    logo: images.linenTechLogo,
    logoHeight: 50,
    accent: '#2563EB',
    accentSecondary: '#1D4ED8',
  },
  {
    key: 'linenconnect',
    name: 'LinenConnect',
    logo: images.linenConnectLogo,
    logoHeight: 40,
    accent: '#15803D',
    accentSecondary: '#21C45D',
  },
  {
    key: 'linendesk',
    name: 'LinenDesk',
    logo: images.linenDeskLogo,
    logoHeight: 38,
    accent: '#1D64D8',
    accentSecondary: '#0E9384',
  },
  {
    key: 'linencrew',
    name: 'LinenCrew',
    logo: images.linenCrewLogo,
    logoHeight: 40,
    accent: '#004EC2',
    accentSecondary: '#0E9540',
  },
  {
    key: 'equipmate',
    name: 'EquipMate',
    logo: images.equipmateLockup,
    logoHeight: 58,
    accent: '#5747FF',
    accentSecondary: '#3B2FD6',
  },
  {
    key: 'lineniq',
    name: 'LinenIQ',
    logo: images.linenIqLockup,
    logoHeight: 58,
    accent: '#0B7A64',
    accentSecondary: '#17B091',
  },
];

/** The unbranded portal identity, and the fallback for any unknown product. */
export const NEXTEK_BRAND: Brand = {
  key: '',
  name: 'Nextek Systems',
  logo: images.headerLogo,
  logoHeight: 48,
  // The indigo the auth cards already use. Never emitted as CSS (see
  // brandCssVars) — it lives here only so callers can read a colour
  // unconditionally.
  accent: '#6366F1',
  accentSecondary: '#8B5CF6',
};

/** `'#2563EB'` -> `'37, 99, 235'`, for the `rgba(var(--accent-rgb), a)` tints. */
const toRgbChannels = (hex: string): string => {
  const value = parseInt(hex.replace('#', ''), 16);
  // eslint-disable-next-line no-bitwise
  return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
};

/**
 * The brand for an `?application=` value. Matched case-insensitively because the
 * casing of that param is not consistent across the suite — the Const key is
 * `linenIq` while the API and the `application` table use `lineniq`.
 */
export const getBrand = (application?: string | null): Brand => {
  if (!application) return NEXTEK_BRAND;
  const lowered = application.toLowerCase();
  return PRODUCT_BRANDS.find((brand) => brand.key === lowered) ?? NEXTEK_BRAND;
};

/** False for the Nextek fallback, i.e. "no product asked for this sign-in". */
export const isProductBrand = (brand: Brand): boolean => brand.key !== '';

/**
 * The accent overrides for a brand, as CSS custom properties to spread onto a
 * card's root element. Every auth stylesheet already reads these, so the cards
 * need no per-brand rules of their own.
 *
 * Empty for Nextek — deliberately. Returning the indigo values instead would
 * overwrite the reset flow's own step colours with login's indigo on the
 * unbranded path, which is a change nobody asked for.
 */
export const brandCssVars = (brand: Brand): Record<string, string> => {
  if (!isProductBrand(brand)) return {};
  return {
    '--accent-primary': brand.accent,
    '--accent-secondary': brand.accentSecondary,
    '--accent-gradient': `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accentSecondary} 100%)`,
    '--accent-rgb': toRgbChannels(brand.accent),
    '--accent-secondary-rgb': toRgbChannels(brand.accentSecondary),
  };
};
